import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import './onboarding.css'

type OnboardingWelcomeProps = {
  onStart: () => void
  onSkip: () => void
}

type PillarId = 'command' | 'govern' | 'execute' | 'align'

const PillarIcon = ({ name }: { name: PillarId }) => {
  if (name === 'command') {
    return (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="2.5" width="5" height="5.5" rx="1.2" />
        <rect x="9" y="2.5" width="5" height="3" rx="1.2" />
        <rect x="9" y="7" width="5" height="6.5" rx="1.2" />
        <rect x="2" y="9.5" width="5" height="4" rx="1.2" />
      </svg>
    )
  }
  if (name === 'govern') {
    return (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M2 3h12l-4.5 5.5V13L6.5 11.5V8.5z" />
      </svg>
    )
  }
  if (name === 'execute') {
    return (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9.5 2.5c2.5 0 4 1.5 4 4L9 11l-2.5.5L5 9.5 9.5 5" />
        <path d="M5.5 10.5L3 13" />
        <circle cx="10.5" cy="5.5" r="1" />
      </svg>
    )
  }
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1.8l5.2 2v4.3c0 3.1-2.2 5.4-5.2 6.1-3-.7-5.2-3-5.2-6.1V3.8z" />
      <path d="M5.9 7.9l1.5 1.5 2.9-3.1" />
    </svg>
  )
}

export const OnboardingWelcome = ({ onStart, onSkip }: OnboardingWelcomeProps) => {
  const { t } = useTranslation()
  const pillars: { id: PillarId; title: string; body: string }[] = [
    { id: 'command', title: t('onboarding.welcome.pillars.command.title'), body: t('onboarding.welcome.pillars.command.body') },
    { id: 'govern', title: t('onboarding.welcome.pillars.govern.title'), body: t('onboarding.welcome.pillars.govern.body') },
    { id: 'execute', title: t('onboarding.welcome.pillars.execute.title'), body: t('onboarding.welcome.pillars.execute.body') },
    { id: 'align', title: t('onboarding.welcome.pillars.align.title'), body: t('onboarding.welcome.pillars.align.body') },
  ]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSkip()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onSkip])

  return (
    <div className="ob-overlay" role="dialog" aria-modal="true" aria-labelledby="ob-welcome-title">
      <div className="ob-card">
        <aside className="ob-rail">
          <div className="ob-rail-mark">INCYIA</div>
          <p className="ob-rail-kicker">{t('onboarding.welcome.kicker')}</p>
          <p className="ob-rail-lead">{t('onboarding.welcome.rail')}</p>
          <div className="ob-rail-orbs" aria-hidden>
            <span />
            <span />
          </div>
        </aside>
        <div className="ob-main">
          <p className="ob-eyebrow">{t('onboarding.welcome.eyebrow')}</p>
          <h1 id="ob-welcome-title" className="ob-title">
            {t('onboarding.welcome.title')}
          </h1>
          <p className="ob-body">{t('onboarding.welcome.body')}</p>
          <div className="ob-pillars ob-pillars--grid">
            {pillars.map((pillar) => (
              <article key={pillar.id} className="ob-pillar">
                <span className="ob-pillar-icon">
                  <PillarIcon name={pillar.id} />
                </span>
                <div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.body}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="ob-foot">
            <span className="ob-duration">{t('onboarding.welcome.duration')}</span>
            <div className="ob-foot-actions">
              <button type="button" className="ob-btn ob-btn--ghost" onClick={onSkip}>
                {t('onboarding.welcome.skip')}
              </button>
              <button type="button" className="ob-btn ob-btn--primary" onClick={onStart} autoFocus>
                {t('onboarding.welcome.start')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
